import { type FC, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { DataTable } from '@/components/data-table'
import { cn } from '@/lib/utils'
import {
    buildFeatureFlagColumns,
    buildFeatureFlagGroupColumns,
} from '@/components/feature-flags/FeatureFlagColumns'
import { CreateEditFlagModal } from '@/components/feature-flags/CreateEditFlagModal'
import { CreateEditGroupModal } from '@/components/feature-flags/CreateEditGroupModal'
import { FlagPreviewDialog } from '@/components/feature-flags/FlagPreviewDialog'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import {
    useCreateFeatureFlag,
    useCreateFeatureFlagGroup,
    useDeleteFeatureFlag,
    useDeleteFeatureFlagGroup,
    useFeatureFlagGroups,
    useFeatureFlags,
    usePreviewFeatureFlagGroup,
    useTargetableFields,
    useToggleFeatureFlag,
    useUpdateFeatureFlag,
    useUpdateFeatureFlagGroup,
} from '@/hooks/useFeatureFlags'
import type {
    CreateFeatureFlagGroupRequest,
    CreateFeatureFlagRequest,
    FeatureFlag,
    FeatureFlagGroup,
    FeatureFlagStatus,
} from '@/types/feature-flag'

type TabType = 'flags' | 'groups'

const tabs: Array<{ id: TabType; label: string }> = [
    { id: 'flags', label: 'Feature flags' },
    { id: 'groups', label: 'Groups' },
]

const STATUS_FILTERS: Array<{
    value: FeatureFlagStatus | 'ALL'
    label: string
}> = [
    { value: 'ALL', label: 'All statuses' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'ARCHIVED', label: 'Archived' },
]

/** `key` is immutable server-side, so it never goes out on an update. */
const toUpdatePayload = <T extends { key: string }>(payload: T) => {
    const next: Partial<T> = { ...payload }
    delete next.key
    return next
}

const errorMessage = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message
        ? [
              (error as { response: { data: { message: string | string[] } } })
                  .response.data.message,
          ]
              .flat()
              .join(', ')
        : fallback

const FeatureFlags: FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('flags')
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState<FeatureFlagStatus | 'ALL'>('ALL')

    const [flagModalOpen, setFlagModalOpen] = useState(false)
    const [groupModalOpen, setGroupModalOpen] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null)
    const [editingGroup, setEditingGroup] = useState<FeatureFlagGroup | null>(
        null
    )
    const [previewFlag, setPreviewFlag] = useState<FeatureFlag | null>(null)
    const [togglingId, setTogglingId] = useState<string | null>(null)
    const [flagPendingDelete, setFlagPendingDelete] =
        useState<FeatureFlag | null>(null)
    const [groupPendingDelete, setGroupPendingDelete] =
        useState<FeatureFlagGroup | null>(null)

    const flagsQuery = useFeatureFlags({
        search: search || undefined,
        status: status === 'ALL' ? undefined : status,
        limit: 100,
    })
    const groupsQuery = useFeatureFlagGroups({ limit: 100 })
    const fieldsQuery = useTargetableFields()

    const createFlag = useCreateFeatureFlag()
    const updateFlag = useUpdateFeatureFlag()
    const toggleFlag = useToggleFeatureFlag()
    const deleteFlag = useDeleteFeatureFlag()
    const createGroup = useCreateFeatureFlagGroup()
    const updateGroup = useUpdateFeatureFlagGroup()
    const deleteGroup = useDeleteFeatureFlagGroup()
    const previewGroup = usePreviewFeatureFlagGroup()

    // The API envelope varies by handler, so never assume an array came back.
    const flags = useMemo(
        () =>
            Array.isArray(flagsQuery.data?.data) ? flagsQuery.data.data : [],
        [flagsQuery.data]
    )
    const groups = useMemo(
        () =>
            Array.isArray(groupsQuery.data?.data) ? groupsQuery.data.data : [],
        [groupsQuery.data]
    )
    const fields = Array.isArray(fieldsQuery.data?.data)
        ? fieldsQuery.data.data
        : []

    const handleToggle = (flag: FeatureFlag) => {
        setTogglingId(flag.id)
        toggleFlag.mutate(
            { id: flag.id, enabled: flag.status !== 'ACTIVE' },
            {
                onSuccess: () =>
                    toast.success(
                        `${flag.name} ${flag.status === 'ACTIVE' ? 'disabled' : 'enabled'}`
                    ),
                onError: error =>
                    toast.error(errorMessage(error, 'Failed to toggle flag')),
                onSettled: () => setTogglingId(null),
            }
        )
    }

    const handleFlagSubmit = (payload: CreateFeatureFlagRequest) => {
        if (editingFlag) {
            updateFlag.mutate(
                { id: editingFlag.id, data: toUpdatePayload(payload) },
                {
                    onSuccess: () => {
                        toast.success('Feature flag updated')
                        setFlagModalOpen(false)
                    },
                    onError: error =>
                        toast.error(
                            errorMessage(error, 'Failed to update flag')
                        ),
                }
            )
            return
        }

        createFlag.mutate(payload, {
            onSuccess: () => {
                toast.success('Feature flag created')
                setFlagModalOpen(false)
            },
            onError: error =>
                toast.error(errorMessage(error, 'Failed to create flag')),
        })
    }

    const handleGroupSubmit = (payload: CreateFeatureFlagGroupRequest) => {
        if (editingGroup) {
            updateGroup.mutate(
                { id: editingGroup.id, data: toUpdatePayload(payload) },
                {
                    onSuccess: () => {
                        toast.success('Group updated')
                        setGroupModalOpen(false)
                    },
                    onError: error =>
                        toast.error(
                            errorMessage(error, 'Failed to update group')
                        ),
                }
            )
            return
        }

        createGroup.mutate(payload, {
            onSuccess: () => {
                toast.success('Group created')
                setGroupModalOpen(false)
            },
            onError: error =>
                toast.error(errorMessage(error, 'Failed to create group')),
        })
    }

    const confirmDeleteFlag = () => {
        if (!flagPendingDelete) return

        deleteFlag.mutate(flagPendingDelete.id, {
            onSuccess: () => {
                toast.success('Feature flag deleted')
                setFlagPendingDelete(null)
            },
            onError: error =>
                toast.error(errorMessage(error, 'Failed to delete flag')),
        })
    }

    const confirmDeleteGroup = () => {
        if (!groupPendingDelete) return

        deleteGroup.mutate(groupPendingDelete.id, {
            onSuccess: () => {
                toast.success('Group deleted')
                setGroupPendingDelete(null)
            },
            onError: error =>
                toast.error(errorMessage(error, 'Failed to delete group')),
        })
    }

    const handlePreviewGroup = (group: FeatureFlagGroup) => {
        previewGroup.mutate(group.id, {
            onSuccess: response =>
                toast.success(
                    `${group.name}: ${response.data.count} user(s)${
                        response.data.capped ? ' (capped at 5,000 scanned)' : ''
                    }`
                ),
            onError: error =>
                toast.error(errorMessage(error, 'Failed to count users')),
        })
    }

    const flagColumns = useMemo(
        () =>
            buildFeatureFlagColumns({
                onToggle: handleToggle,
                onEdit: flag => {
                    setEditingFlag(flag)
                    setFlagModalOpen(true)
                },
                onPreview: flag => {
                    setPreviewFlag(flag)
                    setPreviewOpen(true)
                },
                onDelete: setFlagPendingDelete,
                togglingId,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [togglingId]
    )

    const groupColumns = useMemo(
        () =>
            buildFeatureFlagGroupColumns({
                onEdit: group => {
                    setEditingGroup(group)
                    setGroupModalOpen(true)
                },
                onPreview: handlePreviewGroup,
                onDelete: setGroupPendingDelete,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-[#0F172A]">
                        Feature flags
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Control what the mobile app shows, without shipping a
                        release.
                    </p>
                </div>

                <Button
                    className="bg-[#17A2B8] hover:bg-[#148c9e]"
                    onClick={() => {
                        if (activeTab === 'flags') {
                            setEditingFlag(null)
                            setFlagModalOpen(true)
                        } else {
                            setEditingGroup(null)
                            setGroupModalOpen(true)
                        }
                    }}
                >
                    <Plus className="mr-1 h-4 w-4" />
                    {activeTab === 'flags' ? 'New flag' : 'New group'}
                </Button>
            </div>

            <div className="overflow-x-auto">
                <div className="flex min-w-max gap-6 border-b border-border pb-0">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                                'relative whitespace-nowrap pb-3 text-sm font-medium transition-colors',
                                activeTab === tab.id
                                    ? 'text-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {tab.label}
                            {activeTab === tab.id && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#17A2B8]" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === 'flags' ? (
                <DataTable
                    columns={flagColumns}
                    data={flags}
                    searchPlaceholder="Search flags..."
                    onSearch={setSearch}
                    debounceSearch
                    enableRowSelection={false}
                    showViewButton={false}
                    toolbarActions={
                        <Select
                            value={status}
                            onValueChange={value =>
                                setStatus(value as FeatureFlagStatus | 'ALL')
                            }
                        >
                            <SelectTrigger className="w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_FILTERS.map(filter => (
                                    <SelectItem
                                        key={filter.value}
                                        value={filter.value}
                                    >
                                        {filter.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    }
                />
            ) : (
                <DataTable
                    columns={groupColumns}
                    data={groups}
                    searchPlaceholder="Search groups..."
                    enableRowSelection={false}
                    showViewButton={false}
                />
            )}

            {/* Keyed so each open starts from the target's current values. */}
            <CreateEditFlagModal
                key={`flag-${flagModalOpen}-${editingFlag?.id ?? 'new'}`}
                open={flagModalOpen}
                onOpenChange={setFlagModalOpen}
                onSubmit={handleFlagSubmit}
                flag={editingFlag}
                groups={groups}
                allFlags={flags}
                fields={fields}
                isSubmitting={createFlag.isPending || updateFlag.isPending}
            />

            <CreateEditGroupModal
                key={`group-${groupModalOpen}-${editingGroup?.id ?? 'new'}`}
                open={groupModalOpen}
                onOpenChange={setGroupModalOpen}
                onSubmit={handleGroupSubmit}
                group={editingGroup}
                fields={fields}
                isSubmitting={createGroup.isPending || updateGroup.isPending}
            />

            <FlagPreviewDialog
                key={`preview-${previewOpen}-${previewFlag?.id ?? 'none'}`}
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                flag={previewFlag}
            />

            <ConfirmDialog
                open={Boolean(flagPendingDelete)}
                onOpenChange={open => !open && setFlagPendingDelete(null)}
                onConfirm={confirmDeleteFlag}
                title="Delete feature flag"
                confirmLabel="Delete flag"
                isLoading={deleteFlag.isPending}
                description={
                    <>
                        <span className="font-semibold text-[#0F172A]">
                            {flagPendingDelete?.name}
                        </span>{' '}
                        will be removed. Any app still asking for{' '}
                        <span className="font-mono text-xs">
                            {flagPendingDelete?.key}
                        </span>{' '}
                        falls back to its built-in default. Archive it instead
                        if the feature has already shipped.
                    </>
                }
            />

            <ConfirmDialog
                open={Boolean(groupPendingDelete)}
                onOpenChange={open => !open && setGroupPendingDelete(null)}
                onConfirm={confirmDeleteGroup}
                title="Delete group"
                confirmLabel="Delete group"
                isLoading={deleteGroup.isPending}
                description={
                    <>
                        <span className="font-semibold text-[#0F172A]">
                            {groupPendingDelete?.name}
                        </span>{' '}
                        will be removed. Flags currently targeting it must be
                        pointed elsewhere first.
                    </>
                }
            />
        </div>
    )
}

export default FeatureFlags
